import { isSupportedCountry, type PhoneSignInChannel } from "@teamna/shared";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import { useId, useMemo } from "react";
import { Button } from "./Button.tsx";
import { ChatIcon } from "./Icons.tsx";
import { controlBorder } from "./control.ts";

/**
 * The phone half of sign-in: a country picker next to the number, and one button per channel.
 * Used by the sign-in screen and by "Add a mobile number" in the profile, so the two read the same.
 *
 * The person picks how the code comes by pressing that button. There is no channel toggle and no
 * separate "Send code": one tap, one decision.
 */

export type Country = { code: CountryCode; name: string; dial: string; flag: string };

/** Regional indicator letters, which every current phone shows as the flag. */
function flagOf(code: string): string {
  return String.fromCodePoint(...[...code.toUpperCase()].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65));
}

function regionNames(): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
}

let cachedCountries: Country[] | null = null;

/** Every country libphonenumber knows that Teamna serves, by English name. */
export function allCountries(): Country[] {
  if (cachedCountries) return cachedCountries;
  const names = regionNames();
  cachedCountries = getCountries()
    .filter(isSupportedCountry)
    .map((code) => ({
      code,
      name: names?.of(code) ?? code,
      dial: `+${getCountryCallingCode(code)}`,
      flag: flagOf(code),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return cachedCountries;
}

/**
 * Time zones that name one country, for the first guess. The browser gives no location without a
 * permission prompt, but the zone of the clock is a strong hint and costs nothing. Only the zones
 * our first businesses' members are likely to be in; everything else falls through to the language
 * region and then to the UAE.
 */
const ZONE_COUNTRY: Record<string, CountryCode> = {
  "Asia/Dubai": "AE",
  "Asia/Muscat": "OM",
  "Asia/Qatar": "QA",
  "Asia/Bahrain": "BH",
  "Asia/Kuwait": "KW",
  "Asia/Riyadh": "SA",
  "Asia/Amman": "JO",
  "Asia/Beirut": "LB",
  "Asia/Damascus": "SY",
  "Asia/Baghdad": "IQ",
  "Asia/Tehran": "IR",
  "Africa/Cairo": "EG",
  "Asia/Karachi": "PK",
  "Asia/Kolkata": "IN",
  "Asia/Calcutta": "IN",
  "Asia/Colombo": "LK",
  "Asia/Dhaka": "BD",
  "Asia/Kathmandu": "NP",
  "Asia/Manila": "PH",
  "Europe/London": "GB",
  "Europe/Dublin": "IE",
  "Europe/Paris": "FR",
  "Europe/Berlin": "DE",
  "Europe/Istanbul": "TR",
  "Europe/Moscow": "RU",
  "Africa/Johannesburg": "ZA",
  "Australia/Sydney": "AU",
  "America/Toronto": "CA",
};

const KNOWN = new Set<string>(getCountries().filter(isSupportedCountry));

/**
 * The country the picker starts on: the platform's answer (Cloudflare knows the visitor's
 * country), then the clock's time zone, then the browser's language region, then the UAE.
 */
export function guessCountry(fromServer: string | null | undefined): CountryCode {
  if (fromServer && KNOWN.has(fromServer)) return fromServer as CountryCode;
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const byZone = zone ? ZONE_COUNTRY[zone] : undefined;
    if (byZone) return byZone;
  } catch {
    // No Intl time zone: carry on with the language.
  }
  if (typeof navigator !== "undefined") {
    for (const language of navigator.languages ?? [navigator.language]) {
      const region = language?.split("-")[1]?.toUpperCase();
      if (region && KNOWN.has(region)) return region as CountryCode;
    }
  }
  return "AE";
}

/**
 * The E.164 number for what was typed, or null when it is not a valid mobile number. A number that
 * starts with `+` carries its own country and wins over the picker; `country` then says which, so
 * the picker can follow.
 */
export function readPhone(typed: string, country: CountryCode): { e164: string; country: CountryCode } | null {
  const text = typed.trim();
  if (!text) return null;
  const parsed = text.startsWith("+") || text.startsWith("00")
    ? parsePhoneNumberFromString(text.replace(/^00/, "+"))
    : parsePhoneNumberFromString(text, country);
  if (!parsed?.isValid()) return null;
  if (parsed.country && !isSupportedCountry(parsed.country)) return null;
  return { e164: parsed.number, country: parsed.country ?? country };
}

/**
 * The number in a sign-in link (`/sign-in?phone=%2B971501234567`), for the field: its country and the
 * national digits. A `+` that is not encoded arrives as a space, and the router reads digits as a
 * number, so a space or bare digits count as a number with its +code. Null when there is no valid
 * mobile number in the link.
 */
export function phoneFromLink(value: unknown): { country: CountryCode; national: string } | null {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const text = String(value).trim();
  if (!text) return null;
  const international = text.startsWith("+") ? text : text.startsWith("00") ? `+${text.slice(2)}` : /^[1-9]\d+$/.test(text) ? `+${text}` : null;
  const parsed = international ? parsePhoneNumberFromString(international) : undefined;
  if (!parsed?.isValid() || !parsed.country || !isSupportedCountry(parsed.country)) return null;
  return { country: parsed.country, national: parsed.nationalNumber };
}

/** The country picker and the number, side by side, under one label. */
export function PhoneNumberField({
  country,
  onCountryChange,
  value,
  onChange,
  error,
  label = "Mobile number",
}: {
  country: CountryCode;
  onCountryChange: (country: CountryCode) => void;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  label?: string;
}) {
  const id = useId();
  const countries = useMemo(() => allCountries(), []);
  const selected = countries.find((entry) => entry.code === country);

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="eyebrow text-ink-2">
        {label}
      </label>
      <div className="flex gap-2">
        {/*
          A real select under a styled face: the phone's own picker opens (a wheel on iOS, a list on
          Android), and the closed control shows only the flag and the code.
        */}
        <div
          className={[
            "sk-field relative flex h-13 shrink-0 items-center gap-1.5 rounded-lg border px-3 text-base text-ink",
            error ? "border-cancelled" : "border-line-strong",
          ].join(" ")}
        >
          <span aria-hidden="true" className="text-[20px] leading-none">
            {selected?.flag}
          </span>
          <span aria-hidden="true">{selected?.dial}</span>
          <svg aria-hidden="true" viewBox="0 0 12 12" className="size-3 text-ink-3" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M3 4.5 6 7.5 9 4.5" />
          </svg>
          <select
            aria-label={`Country, ${selected?.name ?? country}, ${selected?.dial ?? ""}`}
            value={country}
            onChange={(event) => onCountryChange(event.target.value as CountryCode)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          >
            {countries.map((entry) => (
              <option key={entry.code} value={entry.code}>
                {`${entry.flag} ${entry.name} (${entry.dial})`}
              </option>
            ))}
          </select>
        </div>
        <input
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder={country === "AE" ? "50 123 4567" : "Mobile number"}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={[
            "sk-field h-13 w-full min-w-0 rounded-lg border px-3.5 text-base text-ink placeholder:text-ink-3",
            "focus:outline-none focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-accent-soft",
            controlBorder(Boolean(error)),
          ].join(" ")}
        />
      </div>
      {error ? (
        <p id={`${id}-error`} className="text-[13px] font-medium text-cancelled">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** The WhatsApp glyph, from Simple Icons (CC0). */
export function WhatsAppIcon({ className = "size-5" }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

/**
 * One button per channel that can carry a code to this number. WhatsApp uses the Button's
 * `whatsapp` variant (WhatsApp's deep green, white text at 5.8:1), never a colour override.
 */
export function PhoneChannelButtons({
  channels,
  loading,
  disabled,
  onSend,
}: {
  channels: PhoneSignInChannel[];
  /** The channel whose send is in flight. */
  loading: PhoneSignInChannel | null;
  disabled?: boolean;
  onSend: (channel: PhoneSignInChannel) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      {channels.map((channel) =>
        channel === "whatsapp" ? (
          <Button
            key={channel}
            variant="whatsapp"
            size="lg"
            fullWidth
            loading={loading === "whatsapp"}
            disabled={disabled || (loading !== null && loading !== "whatsapp")}
            onClick={() => onSend("whatsapp")}
          >
            <WhatsAppIcon />
            Continue with WhatsApp
          </Button>
        ) : (
          <Button
            key={channel}
            variant="secondary"
            size="lg"
            fullWidth
            loading={loading === "sms"}
            disabled={disabled || (loading !== null && loading !== "sms")}
            onClick={() => onSend("sms")}
          >
            <ChatIcon size={20} />
            Continue with SMS
          </Button>
        ),
      )}
    </div>
  );
}

/**
 * The channels to offer for this country: the ones the API says really carry a code, less SMS
 * where SMS may not go.
 */
export function channelsFor(
  country: CountryCode,
  options: { phoneChannels: PhoneSignInChannel[]; smsCountries: string[] },
): PhoneSignInChannel[] {
  return options.phoneChannels.filter((channel) => channel !== "sms" || options.smsCountries.includes(country));
}
